/**
 * ReactRenderer - Compila e renderiza componentes React/JSX em sandbox iframe.
 *
 * Stack disponivel no componente:
 *   - React 18 (useState, useEffect, useRef, useMemo, useCallback, etc.)
 *   - ReactDOM 18
 *   - Tailwind CSS
 *   - Lucide React (icones via window.lucideReact ou const Icons = window.lucideReact)
 *   - Babel Standalone (compila JSX em runtime)
 *
 * Por que esm.sh para lucide-react?
 *   O UMD oficial (jsdelivr) nao funciona no browser sem bundler: tenta acessar
 *   React.forwardRef antes de o React estar resolvido no escopo UMD, gerando
 *   "can't access property forwardRef, i is undefined".
 *   O esm.sh resolve a dependencia automaticamente via ?deps=react@18.3.1.
 *
 * Timing (<script type="module"> e assíncrono):
 *   O modulo ESM usa postMessage() para sinalizar 'lucide-ready' — evita Illegal constructor
 *   ao tentar instanciar CustomEvent em um iframe sandboxed sem allow-same-origin (null-origin realm).
 *   O script classico aguarda essa mensagem (com fallback de 3s) antes de montar.
 */

export class ReactRenderer {

    private static preprocessSource(source: string): string {
        return source
            .replace(/^import\s[\s\S]*?from\s+['"][^'"]+['"]\s*;?(?:\n|$)/gm, '')
            .replace(/^import\s+['"][^'"]+['"]\s*;?(?:\n|$)/gm, '')
            .replace(/^export\s+default\s+/gm, '')
            .replace(/^export\s+(const|let|var|function|class|async\s+function)\b/gm, '$1');
    }

    static renderToHtml(source: string): string {
        const processed = ReactRenderer.preprocessSource(source);
        const escaped = processed.replace(/<\/script>/gi, '<\\/script>');
        const sourceJson = JSON.stringify(escaped);

        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<script src="https://cdn.jsdelivr.net/npm/react@18.3.1/umd/react.production.min.js" crossorigin><\/script>
<script src="https://cdn.jsdelivr.net/npm/react-dom@18.3.1/umd/react-dom.production.min.js" crossorigin><\/script>
<script>
window.React    = (typeof React    !== 'undefined') ? React    : { forwardRef: function(r){return r;}, createElement: function(){return null;}, __stub: true };
window.ReactDOM = (typeof ReactDOM !== 'undefined') ? ReactDOM : {};
<\/script>
<script type="module">
import * as LucideReact from 'https://esm.sh/lucide-react@0.468.0?deps=react@18.3.1';
window.lucideReact = LucideReact;
window.postMessage({ type: '__lucide_ready__' }, '*');
<\/script>
<script src="https://cdn.jsdelivr.net/npm/@babel/standalone@7/babel.min.js"><\/script>
<script src="https://cdn.tailwindcss.com"><\/script>
<style>
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; }
body { padding: 16px; background: transparent; min-height: 50px; }
#root { min-height: 50px; }
.artifact-error {
    color: #dc2626; background: #fef2f2; border: 1px solid #fecaca;
    border-radius: 8px; padding: 16px; font-family: monospace; font-size: 13px;
    white-space: pre-wrap; word-break: break-all;
}
.artifact-error strong { display: block; font-family: system-ui; font-size: 14px; margin-bottom: 8px; }
<\/style>
<\/head>
<body>
<div id="root"><\/div>
<script>
window.addEventListener('error', function(e) {
    var root = document.getElementById('root');
    if (root && !root.querySelector('.artifact-error')) {
        root.innerHTML = '<div class="artifact-error"><strong>&#9888; Erro<\/strong>' + (e.message || 'Erro desconhecido') + '<\/div>';
    }
});

function _showErr(msg) {
    document.getElementById('root').innerHTML = '<div class="artifact-error"><strong>&#9888; ' + msg + '<\/strong><\/div>';
}

function mountApp() {
    if (window.React && window.React.__stub) {
        _showErr('React nao disponivel. Falha ao carregar via CDN.');
        return;
    }

    var userSource = ${sourceJson};
    var compiled;

    try {
        compiled = Babel.transform(userSource, { presets: ['react'], filename: 'component.jsx' }).code;
    } catch (e) {
        _showErr('Erro de compilacao JSX: ' + (e.message || String(e)));
        return;
    }

    var lines = [
        'var _R = window.React;',
        'var useState=_R.useState, useEffect=_R.useEffect, useRef=_R.useRef,',
        '    useMemo=_R.useMemo, useCallback=_R.useCallback, useReducer=_R.useReducer,',
        '    useContext=_R.useContext, createContext=_R.createContext, memo=_R.memo,',
        '    Fragment=_R.Fragment, forwardRef=_R.forwardRef,',
        '    useImperativeHandle=_R.useImperativeHandle, useLayoutEffect=_R.useLayoutEffect,',
        '    useId=_R.useId;',
        'var Icons = window.lucideReact || {};',
        compiled,
        '(function() {',
        '    var names = ["App","Component","Main","Page","Widget","Dashboard","Layout","Screen"];',
        '    var Root = null;',
        '    for (var i = 0; i < names.length; i++) {',
        '        try { var c = eval(names[i]); if (typeof c === "function") { Root = c; break; } } catch(e) {}',
        '    }',
        '    if (!Root) {',
        '        var keys = Object.keys(window).filter(function(k) {',
        '            return /^[A-Z][a-zA-Z]/.test(k) && typeof window[k] === "function"',
        '                && ["React","ReactDOM","Babel"].indexOf(k) === -1;',
        '        });',
        '        if (keys.length) Root = window[keys[keys.length - 1]];',
        '    }',
        '    if (Root) {',
        '        try {',
        '            ReactDOM.createRoot(document.getElementById("root")).render(React.createElement(Root));',
        '        } catch(e) {',
        '            _showErr("Erro ao renderizar: " + (e.message || String(e)));',
        '        }',
        '    } else {',
        '        _showErr("Componente nao encontrado. Declare um componente PascalCase (App, Component, etc.).");',
        '    }',
        '})()',
    ];

    var scriptEl = document.createElement('script');
    scriptEl.textContent = lines.join('\\n');
    document.body.appendChild(scriptEl);
}

var _lt = setTimeout(function() {
    window.lucideReact = window.lucideReact || {};
    mountApp();
}, 3000);

window.addEventListener('message', function(e) {
    if (e.data && e.data.type === '__lucide_ready__') {
        clearTimeout(_lt);
        mountApp();
    }
}, { once: true });
<\/script>
<\/body>
<\/html>`;
    }
}
